<template>
    <div class="admin-panel moderation-panel">

        <div class="moderation-tabs">
            <div
                v-for="(group, groupIndex) in tabGroups"
                :key="group.key"
                class="moderation-tabs__group"
                :class="[
                    `moderation-tabs__group--${group.key}`,
                    { 'moderation-tabs__group--disabled': group.disabled },
                ]"
            >
                <div
                    v-if="groupIndex > 0"
                    class="moderation-tabs__divider"
                    aria-hidden="true"
                ></div>
                <div class="moderation-tabs__group-inner">
                    <div class="moderation-tabs__group-head">
                        <span class="moderation-tabs__group-label">{{ group.label }}</span>
                        <span v-if="getGroupCount(group) > 0" class="moderation-tabs__group-count">
                            {{ getGroupCount(group) }}
                        </span>
                    </div>
                    <div class="moderation-tabs__row">
                        <button
                            v-for="tab in group.tabs"
                            :key="tab.id"
                            type="button"
                            :class="[
                                'moderation-tab',
                                {
                                    active: activeTab === tab.id,
                                    'moderation-tab--disabled': group.disabled,
                                },
                            ]"
                            :aria-disabled="group.disabled ? 'true' : undefined"
                            :disabled="Boolean(group.disabled)"
                            @click="selectTab(tab.id, group)"
                        >
                            {{ tab.label }}
                            <span v-if="getTabCount(tab.id) > 0" class="moderation-tab__count">
                                {{ getTabCount(tab.id) }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="admin-content">
            <CampaignModeration v-if="activeTab === 'campaign-moderation'" :user="userData"
                :initial-campaigns="initialCampaigns" />
            <Advertisement-moderation v-if="activeTab === 'advertisement-moderation'" :user="userData"
                :initial-advertisements="initialAdvertisements"
                :initial-advertisements-pagination="initialAdvertisementsPagination" />
            <CreativeModeration v-if="activeTab === 'creative-moderation'" :user="userData"
                :initial-creatives="initialCreatives" :initial-creatives-pagination="initialCreativesPagination"
                :organizations="organizations" />
            <RtbCampaignModeration v-if="activeTab === 'rtb-campaign-moderation'" :user="userData"
                :initial-campaigns="initialRtbCampaigns" />
            <RtbCreativeModeration v-if="activeTab === 'rtb-creative-moderation'" :user="userData"
                :initial-creatives="initialRtbCreatives"
                :initial-next-cityscreen-api-id="initialNextCityscreenApiId" />
        </div>
    </div>
</template>

<script>
import CampaignModeration from '../campaigns/moderation/CampaignModeration.vue';
import AdvertisementModeration from '../advertisements/AdvertisementModeration.vue';
import CreativeModeration from '../creatives/moderation/CreativeModeration.vue';
import RtbCreativeModeration from '../creatives/moderation/RtbCreativeModeration.vue';
import RtbCampaignModeration from '../campaigns/moderation/RtbCampaignModeration.vue';

export default {
    name: 'ModerationPanel',
    components: {
        CampaignModeration,
        AdvertisementModeration,
        CreativeModeration,
        RtbCreativeModeration,
        RtbCampaignModeration,
    },
    props: {
        userData: {
            type: Object,
            required: true,
        },
        initialRtbCampaigns: {
            type: Array,
            default: () => [],
        },
        initialCampaigns: {
            type: Array,
            default: () => [],
        },
        initialAdvertisements: {
            type: Array,
            default: () => [],
        },
        initialAdvertisementsPagination: {
            type: Object,
            default: null,
        },
        initialCreatives: {
            type: Array,
            default: () => [],
        },
        initialCreativesPagination: {
            type: Object,
            default: null,
        },
        organizations: {
            type: Array,
            default: () => [],
        },
        initialRtbCreatives: {
            type: Array,
            default: () => [],
        },
        initialNextCityscreenApiId: {
            type: Number,
            default: 1,
        },
        initialTabCounts: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            activeTab: 'rtb-campaign-moderation',
            tabGroups: [
                {
                    key: 'rtb',
                    label: 'RTB',
                    disabled: false,
                    tabs: [
                        { id: 'rtb-campaign-moderation', label: 'Кампании' },
                        { id: 'rtb-creative-moderation', label: 'Креативы' },
                    ],
                },
                {
                    key: 'guaranteed',
                    label: 'Гарантированный',
                    disabled: true,
                    tabs: [
                        { id: 'campaign-moderation', label: 'Кампании' },
                        { id: 'advertisement-moderation', label: 'Объявления' },
                        { id: 'creative-moderation', label: 'Креативы' },
                    ],
                },
            ],
        };
    },
    methods: {
        selectTab(tabId, group) {
            if (group?.disabled) return;
            this.activeTab = tabId;
        },
        getTabCount(tabId) {
            const value = this.initialTabCounts?.[tabId];
            const n = Number(value);
            return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
        },
        getGroupCount(group) {
            return (group.tabs || []).reduce((sum, tab) => sum + this.getTabCount(tab.id), 0);
        },
    },
};
</script>

<style lang="scss">
@use '../../../scss/pages/admin/admin-panel' as *;
</style>

<style lang="scss" scoped>
.moderation-tabs {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0;
    border-bottom: 2px solid #e5e7eb;
    margin-bottom: 0;
}

.moderation-tabs__group {
    display: flex;
    align-items: flex-end;
}

.moderation-tabs__divider {
    width: 1px;
    align-self: stretch;
    margin: 8px 16px 0;
    background: #d1d5db;
    flex-shrink: 0;
}

.moderation-tabs__group-inner {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 12px 0;
    border: 1px solid transparent;
    border-bottom: none;
    border-radius: 10px 10px 0 0;
}

.moderation-tabs__group--guaranteed .moderation-tabs__group-inner {
    background: #f8fafc;
    border-color: #e2e8f0;
}

.moderation-tabs__group--rtb .moderation-tabs__group-inner {
    background: #f5f3ff;
    border-color: #ddd6fe;
}

.moderation-tabs__group--disabled {
    opacity: 0.6;

    .moderation-tabs__group-inner {
        pointer-events: none;
    }

    .moderation-tabs__group-label {
        color: #9ca3af;
    }
}

.moderation-tabs__group-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 4px;
}

.moderation-tabs__group-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6b7280;
}

.moderation-tabs__group--rtb .moderation-tabs__group-label {
    color: #6d28d9;
}

.moderation-tabs__group-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    font-size: 11px;
    font-weight: 600;
    color: #fff;
    background: #ef4444;
    border-radius: 9px;
}

.moderation-tabs__row {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.moderation-tab {
    position: relative;
    padding: 0.75rem 1.25rem;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    font-size: 0.95rem;
    font-weight: 500;
    color: #6b7280;
    transition: color 0.2s, border-color 0.2s;
    margin-bottom: -2px;
    white-space: nowrap;

    &:hover {
        color: #007bff;
    }

    &.active {
        color: #007bff;
        border-bottom-color: #007bff;
    }
}

.moderation-tabs__group--rtb .moderation-tab {
    &:hover {
        color: #7c3aed;
    }

    &.active {
        color: #7c3aed;
        border-bottom-color: #7c3aed;
    }
}

.moderation-tab--disabled {
    color: #9ca3af;
    cursor: not-allowed;

    &:hover {
        color: #9ca3af;
    }

    &.active {
        color: #9ca3af;
        border-bottom-color: transparent;
    }
}

.moderation-tab__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    margin-left: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    background: #ef4444;
    border-radius: 10px;
}

@media (max-width: 768px) {
    .moderation-tabs {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
        padding-bottom: 12px;
        border-bottom: none;
    }

    .moderation-tabs__group {
        flex-direction: column;
        align-items: stretch;
    }

    .moderation-tabs__divider {
        width: 100%;
        height: 1px;
        margin: 0;
    }

    .moderation-tabs__group-inner {
        border-radius: 10px;
        border-bottom: 1px solid;
        padding-bottom: 8px;
    }

    .moderation-tabs__group--guaranteed .moderation-tabs__group-inner {
        border-bottom-color: #e2e8f0;
    }

    .moderation-tabs__group--rtb .moderation-tabs__group-inner {
        border-bottom-color: #ddd6fe;
    }

    .moderation-tab {
        margin-bottom: 0;
    }
}
</style>
